import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import config from '@/config';
import useStateHandler from '@/hooks/useStateHandler';
import AdminService from '@/services/admin.service';
import AddArtworkForm from '@/shared-components/forms/add-artwork';
import Text from '@/shared-components/text/components/Text';

import DenyModal from './components/DenyModal';
import RequestChangesModal from './components/RequestChangesModal';

import './index.css';

const MADRID_TIMEZONE = 'Europe/Madrid';

function formatScheduledPublication(isoString, timeZone) {
  if (!isoString) {
    return null;
  }

  const parsed = new Date(isoString);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  const targetZone = timeZone || MADRID_TIMEZONE;

  try {
    return new Intl.DateTimeFormat('en-GB', {
      dateStyle: 'full',
      timeStyle: 'short',
      timeZone: targetZone
    }).format(parsed);
  } catch (error) {
    return new Intl.DateTimeFormat('en-GB', {
      dateStyle: 'full',
      timeStyle: 'short'
    }).format(parsed);
  }
}

export default function ArtworkRequest() {
  const { cacheHandler } = useStateHandler();
  const [loading, setLoading] = useState(false);
  const [openRequestChangesModal, setOpenRequestChangesModal] = useState(false);
  const [openDenyModal, setOpenDenyModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  if(!(location.state?.draftData)) {
    return <Navigate to="/admin/artwork-requests" replace/>;
  }

  async function acceptArtworkRequest() {
    setLoading(true);
    const { response } = await AdminService.acceptArtworkRequest(location.state.draftData._id);
    setLoading(false);

    if(response.ok) {
      alert("La solicitud de la obra ha sido aceptada correctamente");
      cacheHandler.triggerAction("ARTWORK_REQUEST_ACCEPTED");
      navigate("/admin/artwork-requests", { replace: true });
    } else {
      alert("Algo ha fallado, vuelve a interlo mas adelante");
    }
  }

  const scheduledPublication = location.state.draftData?.draft_container?.publishedAt
    ?? location.state.draftData?.publishedAt
    ?? null;
  const scheduledTimezone = location.state.draftData?.draft_container?.publicationTimezone
    ?? location.state.draftData?.publicationTimezone
    ?? null;
  const formattedScheduledPublication = formatScheduledPublication(
    scheduledPublication,
    scheduledTimezone
  );

  return (
    <>
      <div className="artwork-request-changes__wrap">
        {formattedScheduledPublication && (
          <div className="artwork-request-changes__scheduled">
            <Text className="artwork-request-changes__scheduled-title font-size-medium">
              Scheduled publication
            </Text>
            <Text className="artwork-request-changes__scheduled-value font-size-medium">
              {formattedScheduledPublication}
            </Text>
          </div>
        )}
        {Boolean(location.state.draftData.draft_container.critical_elements_changed) && (
          <div className="artwork-request-changes__critical-changes-container">
            <h1 className="font-size-medium">EN ESTA OBRA SE HAN PRODUCIDO CAMBIOS EN CAMPOS QUE HAN SIDO CONSIDERADOS CRITICOS (CAMPOS DONDE ES NECESARIO UNA REVISION).</h1>
            <p className="font-size-medium">
  Los campos que se consideran criticos son:
            </p>
            <ul>
              <li className="font-size-medium">
    +-5cm en length o height
              </li>
              <li className="font-size-medium">
    Cambio de artista
              </li>
              <li className="font-size-medium">
    Cambio la foto principal
              </li>
            </ul>
            <p className="font-size-medium">
  Estos son los cambios que han ocurrido:
            </p>

            {location.state.draftData.draft_container.critical_elements_changed.map(criticalChange => (
              <p className="font-size-medium" key={criticalChange.element}>
                Ha cambiado {criticalChange.element}. Antes valia:{" "}

                {
                  criticalChange.element === "artwork_artist" ?
                    `${criticalChange.old_value.gallery_artist ? 
                      criticalChange.old_value.user_name 
                      : 
                      criticalChange.old_value.artist_name}`
                    : criticalChange.element === "artwork_size" ?
                      `Length: ${criticalChange.old_value.length}, Height: ${criticalChange.old_value.height}, Unit: ${criticalChange.old_value.unit}`
                      :
                      <a href={`${config.app.imageServiceDomain}/${criticalChange.old_value}/gamma=0`} target="_blank" rel="noopener noreferrer">
                        Ver imagen anterior
                      </a>
                }
              </p>
            ))}
          </div>
        )}

        <AddArtworkForm artworkData={location.state.draftData} previewMode/>
        <div className="artwork-request-changes__options">
          <Text
            className={[
              `artwork-request-changes__button accept ${loading ? "read-only" : ""}`,
              "font-size-small"
            ].filter(Boolean).join(" ")}
            onClick={acceptArtworkRequest}>
            {loading ? "Loading..." : "Accept artwork request"}
          </Text>

          <Text
            className={[
              `artwork-request-changes__button request ${loading ? "read-only" : ""}`,
              "font-size-small"
            ].filter(Boolean).join(" ")}
            onClick={()=>setOpenRequestChangesModal(true)}>
            {loading ? "Loading..." : "Changes Required"}
          </Text>

          <Text
            className={[
              `artwork-request-changes__button deny ${loading ? "read-only" : ""}`,
              "font-size-small"
            ].filter(Boolean).join(" ")}
            onClick={()=>setOpenDenyModal(true)}>
            Deny artwork request
          </Text>
        </div>
      </div>
      <RequestChangesModal open={openRequestChangesModal} close={()=>setOpenRequestChangesModal(false)} draftId={location.state.draftData._id}/>
      <DenyModal open={openDenyModal} close={()=>setOpenDenyModal(false)} draftId={location.state.draftData._id}/>
    </>
  );
}
